import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { getAndClearPostLoginRedirect } from '../helpers/storage/storage';

/**
 * Custom hook that handles post-login redirect logic.
 *
 * When a user attempts to access a protected route with an expired token,
 * the intended destination is stored in localStorage. After successful login,
 * this hook checks for that stored path and redirects the user there instead
 * of the default landing page.
 *
 * @param {boolean} shouldCheckRedirect - Whether to check for a stored redirect path (typically true when authenticated)
 * @returns {boolean} - True while checking/navigating to stored path, false when default route should be used
 *
 */
function usePostLoginRedirect(shouldCheckRedirect: boolean): boolean {
  const navigate = useNavigate();
  const [useRedirect, setUseRedirect] = useState(true);

  useEffect(() => {
    if (shouldCheckRedirect) {
      // Check for stored redirect path and navigate if exists
      const redirectPath = getAndClearPostLoginRedirect();

      if (redirectPath) {
        // Navigate to stored path - keep useRedirect true
        // because we're navigating away from this component
        navigate(redirectPath, { replace: true });
      } else {
        // No redirect path found - allow default navigation
        setUseRedirect(false);
      }
    } else {
      // Reset when condition is not met (e.g., user logs out)
      setUseRedirect(true);
    }
  }, [shouldCheckRedirect, navigate]);

  return useRedirect;
}

export { usePostLoginRedirect };
